import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { BundleOwner } from '@prisma/client';
import { Breadcrumb, Button, Descriptions, Form, Layout, message, Result, Space, Spin, theme, Tooltip } from 'antd';

import { SubscriptionAccessWithChildren } from '@app/domain-subscription-interfaces';
import { formatIsoDate } from '@app/utils-agnostic';
import { Auth, PageLayout } from '@app/web-components';

import { EligibilityRenderer, useAdvancedFindSubscriptionAccess } from '../../bundles';
import { NotFoundPage } from '../../common';
import { useDeleteSubscriptionAccess, useFindWorker } from '../data';

import { SUBSCRIPTIONS_LANDING_TITLE } from './landing';

export const RemoveAccessPage = () => {
  const { id } = useParams();
  const { user } = Auth.useUserAuth();
  const {
    token: { colorBgContainer },
  } = theme.useToken();
  const navigate = useNavigate();
  const [messageApi, messageContextHolder] = message.useMessage();
  const deleteSubscriptionAccess = useDeleteSubscriptionAccess();

  const { data, isPending, isError } = useAdvancedFindSubscriptionAccess<
    SubscriptionAccessWithChildren & {
      subscription: { bundle: { name: string; owners: BundleOwner[] } };
    }
  >({
    where: {
      id: Number(id),
    },
    include: {
      subscription: {
        select: {
          user: { select: { id: true, enterpriseId: true, givenName: true, familyName: true } },
          bundle: {
            select: {
              name: true,
              createdBy: true,
              owners: { select: { ownerId: true } },
            },
          },
        },
      },
      bundleAccess: {
        select: { access: { select: { name: true } } },
      },
    },
  });

  // Get Subscriber's manager info if currently logged in user is not the subscriber
  const {
    data: subscriber,
    isInitialLoading: isInitialLoadingWorker, //Refer: https://tanstack.com/query/v4/docs/react/guides/disabling-queries
    isError: isErrorWorker,
  } = useFindWorker(data?.data[0]?.subscription.user.enterpriseId ?? '', {
    enabled:
      !!data &&
      !!user?.enterpriseId &&
      !!data?.data[0]?.subscription.user.enterpriseId &&
      data?.data[0]?.subscription.user.enterpriseId !== '' &&
      data?.data[0]?.subscription.user.enterpriseId !== user?.enterpriseId,
  });

  // wait for all data before all checks ()
  if (isPending || isInitialLoadingWorker) {
    return <Spin style={{ width: '100%' }} />;
  }

  if (isError || !data) {
    return (
      <NotFoundPage subTitle="Sorry, this subscription access does not exist or has been removed.">
        <Button type="primary" onClick={() => navigate('/')}>
          Back Home
        </Button>
      </NotFoundPage>
    );
  }

  // if query to get subscriber manager info failed, show error in toast before navigating to 403 page
  if (isErrorWorker) {
    messageApi.error('Failed to get subscriber manager info from worker service.');
  }

  if (
    user?.enterpriseId !== data.data[0]?.subscription?.user?.enterpriseId && // not the subscriber
    user?.enterpriseId !== subscriber?.nextLevelWwid // not subscriber's manager
  ) {
    return <Navigate to="/access-forbidden" />;
  }

  if (deleteSubscriptionAccess.isSuccess) {
    return (
      <Result
        status="success"
        title="Successfully deleted subscription access!"
        subTitle={`Request submited successfully to AGS for access removal`}
        extra={[
          <Button type="primary" key="go to bundles" onClick={() => navigate('../')}>
            Go To Subscriptions
          </Button>,
        ]}
      />
    );
  }

  return (
    <>
      <Breadcrumb
        style={{ margin: '16px 0' }}
        items={[
          { title: <Link to="/">Home</Link> },
          { title: <Link to="../">{SUBSCRIPTIONS_LANDING_TITLE}</Link> },
          { title: 'Remove Access' },
        ]}
      />
      <Layout.Content
        style={{
          padding: 24,
          margin: 'auto',
          height: '100%',
          width: '100%',
          maxWidth: 1200,
          minHeight: 0,
          background: colorBgContainer,
          overflow: 'auto',
        }}
      >
        {messageContextHolder}
        <PageLayout.Title title={`Remove Subscription Access`} />
        <Form>
          <Descriptions bordered size="small" layout="horizontal" column={2}>
            <Descriptions.Item label="Access"> {data.data[0].bundleAccess?.access?.name}</Descriptions.Item>
            <Descriptions.Item label="Bundle">{data.data[0].subscription.bundle.name}</Descriptions.Item>
            <Descriptions.Item label="Subscribed For">
              {data.data[0].subscription.user.familyName}, {data.data[0].subscription.user.givenName}
            </Descriptions.Item>
            <Descriptions.Item label="Subscribed On">
              {formatIsoDate(data.data[0].createdOn.toString())}
            </Descriptions.Item>
            <Descriptions.Item label="Eligibility">
              <EligibilityRenderer isEligible={data.data[0].isEligible} eligibility={data.data[0].eligibility} />
            </Descriptions.Item>
          </Descriptions>
          <Space style={{ marginTop: 60 }}>
            <Tooltip
              placement="rightTop"
              title={`This will remove the subscriber's access in AGS and from all currently subscribed bundles for the user`}
            >
              <Button
                type="primary"
                danger
                disabled={isErrorWorker || !deleteSubscriptionAccess.isIdle}
                loading={isPending}
                onClick={() =>
                  deleteSubscriptionAccess.mutate(Number(id), {
                    onError: (error) => {
                      messageApi.open({
                        type: 'error',
                        content: `Failed to remove subscription access '${data.data[0]?.bundleAccess.access.name}'... Error: ${error}`,
                      });
                    },
                  })
                }
              >
                Remove Access Now
              </Button>
            </Tooltip>
          </Space>
        </Form>
      </Layout.Content>
    </>
  );
};
